/**
 * Signing in with a SYQEL account (the shared syqel-visualizer identity that SYQEL Live, Desktop and
 * Studio use) and keeping its platform entitlement token. A plan bought on syqel.com/pricing is recorded
 * on the ACCOUNT (`apps.art`), not in syqel.art's own Stripe cookie — this is the only way the web and
 * desktop players see it. Same calls SYQEL Live makes: Firebase REST sign-in → `/v1/claim` → the signed
 * token, verified against the pinned key BEFORE it is stored; the Firebase refresh token renews it.
 *
 * catalogue.ts refreshEntitlement() is the one owner of "is this Art paid" — it asks accountArtPaid()
 * beside syqel.art's own checkout. Nothing here writes the entitlement cache.
 */
import { verifyAsync } from '@noble/ed25519'
import { adoptSyqelToken, artPaid, readSyqelToken, type SyqelClaims } from './syqelToken'

const PLATFORM = 'https://syqel-platform.syqel-inc.workers.dev'
const FIREBASE_KEY = 'AIzaSyCeBsJ8aBsqCZTuDjvY24llso1hznwn588' // public web config (syqel-visualizer)
const IDENTITY = 'https://identitytoolkit.googleapis.com/v1'
const SECURE_TOKEN = 'https://securetoken.googleapis.com/v1/token'
const STORE_KEY = 'syqel.account'
const DEVICE_KEY = 'syqel.deviceId'

interface Stored {
  token: string
  refresh: string | null
  email: string
}

const verify = (sig: Uint8Array, msg: Uint8Array, pub: Uint8Array): Promise<boolean> => verifyAsync(sig, msg, pub)
const nowSec = (): number => Math.floor(Date.now() / 1000)

function load(): Stored | null {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as Stored
    return typeof s.token === 'string' && typeof s.email === 'string' ? s : null
  } catch {
    return null
  }
}

function save(s: Stored | null): void {
  try {
    if (s) localStorage.setItem(STORE_KEY, JSON.stringify(s))
    else localStorage.removeItem(STORE_KEY)
  } catch { /* private mode: the account lasts this session */ }
}

function deviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(DEVICE_KEY, id)
    }
    return id
  } catch {
    return 'art-web'
  }
}

export type SignInResult = 'ok' | 'not-paid' | 'wrong-credentials' | 'error'

async function claim(idToken: string, desktop: boolean): Promise<string | null> {
  const res = await fetch(`${PLATFORM}/v1/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      idToken,
      device: { id: deviceId(), name: 'SYQEL Art', platform: desktop ? 'desktop' : 'web', app: 'art' }
    })
  })
  if (!res.ok) return null
  const data = (await res.json()) as { ok?: boolean; token?: string }
  return data.ok && typeof data.token === 'string' ? data.token : null
}

/** Keep a freshly issued token if it verifies (and doesn't wrongly drop a running Art plan). */
async function adopt(token: string, refresh: string | null, email: string): Promise<SyqelClaims | null> {
  const fresh = await readSyqelToken(token, verify)
  if (!fresh) return null
  const prev = load()
  const prevClaims = prev ? await readSyqelToken(prev.token, verify) : null
  const keep = adoptSyqelToken(prevClaims, fresh, nowSec()) === 'prev' && prev ? prev.token : token
  save({ token: keep, refresh, email })
  return keep === token ? fresh : prevClaims
}

/** Email + password sign-in to the SYQEL account (the password set in SYQEL Live / Desktop / syqel.com). */
export async function signInSyqelAccount(email: string, password: string, desktop: boolean): Promise<SignInResult> {
  try {
    const si = await fetch(`${IDENTITY}/accounts:signInWithPassword?key=${FIREBASE_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, returnSecureToken: true })
    })
    if (!si.ok) {
      const body = (await si.json().catch(() => ({}))) as { error?: { message?: string } }
      const msg = body.error?.message ?? ''
      return /PASSWORD|EMAIL_NOT_FOUND|INVALID_LOGIN|INVALID_EMAIL|USER_DISABLED/.test(msg) ? 'wrong-credentials' : 'error'
    }
    const { idToken, refreshToken, email: canonical } = (await si.json()) as { idToken?: string; refreshToken?: string; email?: string }
    if (!idToken) return 'error'
    const token = await claim(idToken, desktop)
    if (!token) return 'error'
    const claims = await adopt(token, refreshToken ?? null, canonical ?? email)
    if (!claims) return 'error'
    return artPaid(claims, nowSec()) ? 'ok' : 'not-paid'
  } catch {
    return 'error'
  }
}

/** Email the SYQEL account's password-reset link (for buyers who never set a password). */
export async function sendSyqelPasswordReset(email: string): Promise<boolean> {
  try {
    const res = await fetch(`${IDENTITY}/accounts:sendOobCode?key=${FIREBASE_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requestType: 'PASSWORD_RESET', email })
    })
    return res.ok
  } catch {
    return false
  }
}

/** The signed-in SYQEL account's email, or null. */
export function syqelAccountEmail(): string | null {
  return load()?.email ?? null
}

export function signOutSyqelAccount(): void {
  save(null)
}

/**
 * Does the signed-in SYQEL account own Art? Renews the token through the stored refresh token when
 * online; offline (or when the platform is unreachable) the stored token decides until it expires.
 */
export async function accountArtPaid(desktop: boolean): Promise<boolean> {
  const stored = load()
  if (!stored) return false
  if (stored.refresh) {
    try {
      const res = await fetch(`${SECURE_TOKEN}?key=${FIREBASE_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ grant_type: 'refresh_token', refresh_token: stored.refresh }).toString()
      })
      if (res.ok) {
        const data = (await res.json()) as { id_token?: string; refresh_token?: string }
        const token = data.id_token ? await claim(data.id_token, desktop) : null
        if (token) {
          const claims = await adopt(token, data.refresh_token ?? stored.refresh, stored.email)
          if (claims) return artPaid(claims, nowSec())
        }
      } else if (res.status === 400) {
        // the account's sign-in was revoked (password changed, account deleted): the stored token
        // still counts until it expires, but it can't renew
        save({ ...stored, refresh: null })
      }
    } catch { /* offline: fall through to the stored token */ }
  }
  const claims = await readSyqelToken(stored.token, verify)
  return claims ? artPaid(claims, nowSec()) : false
}
